import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Pagination from "@material-ui/lab/Pagination";
import { Typography } from "@material-ui/core";
import {
  setTotalFiles,
  setAreaGroup,
  setCurrentPageNumber,
} from "../../actions";

const useStyles = makeStyles((theme) =>
  createStyles({
    tableWrapper: {
      width: "100%",
    },
    tableWrapperSlim: {
      width: "89%",
    },
    table: {
      width: "100%",
    },
    tableHead: {
      height: 40,
      backgroundColor: "#fafafa",
    },
    tableRow: {
      height: 126,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    tableRowNone: {
      height: 126,
    },
    noDataCell: {
      textAlign: "center",
      padding: theme.spacing(3),
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
    tableBlock: {
      width: "100%",
    },
    tableBlockHeader: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      marginBottom: 6, // テーブル枠との間隔
    },
    tableNote: {
      whiteSpace: "nowrap",
    },
    fileMeta: {
      marginLeft: theme.spacing(1),
      color: theme.palette.text.secondary,
      fontSize: 12,
      whiteSpace: "nowrap",
    },
  })
);

const normalizeAreaValue = (value) => {
  if (typeof value !== "string") return "";
  return value.replace(/（\d+件）$/, "").trim();
};

const normalizeSelectedAreas = (value) => {
  if (Array.isArray(value)) return value.map(normalizeAreaValue).filter(Boolean);
  if (typeof value === "string") return [normalizeAreaValue(value)].filter(Boolean);
  return [];
};

const DownloadTable = ({
  setCurrentPageNumber,
  open,
  currentPageNumber,
  setTotalFiles,
  setAreaGroup,
  filterDownload,
  onFileNameOptionsChange,
  onFilteredCountChange,
}) => {
  const classes = useStyles();
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Redux に保存しているページ番号 → ローカル state に反映
  useEffect(() => {
    setCurrentPage(currentPageNumber || 1);
  }, [currentPageNumber]);

  // download.json を読み込む（初回のみ）
  useEffect(() => {
    const fetchDownloadList = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_PICTREE_DATA_BASE_URL}/tepco/download/download.json`
        );
        if (!res.ok) {
          throw new Error(`Failed to load download.json: ${res.status}`);
        }
        const data = await res.json();
        const displayData = Array.isArray(data) ? data : [];

        setTableData(displayData);

        if (typeof onFileNameOptionsChange === "function") {
          const options = Array.from(
            new Set(
              displayData
                .flatMap((item) => String(item.fileName || "").split(/[\s._/-]+/))
                .map((token) => token.trim())
                .filter(Boolean)
            )
          ).sort((a, b) => a.localeCompare(b, "ja"));
          onFileNameOptionsChange(options);
        }

        // 全件数
        setTotalFiles(displayData.length);

        // 県域ごとの件数
        const areaCountMap = displayData.reduce((acc, item) => {
          if (!item.area) return acc;
          acc[item.area] = (acc[item.area] || 0) + 1;
          return acc;
        }, {});

        const areaOptions = [
          {
            value: "all",
            label: "全て",
            count: displayData.length,
          },
          ...Object.entries(areaCountMap).map(([area, count]) => ({
            value: area,
            label: `${area}（${count}件）`,
            count,
          })),
        ];

        setAreaGroup(areaOptions);
      } catch (err) {
        console.error("Error loading download list:", err);
        setTableData([]);
        if (typeof onFileNameOptionsChange === "function") {
          onFileNameOptionsChange([]);
        }
        setTotalFiles(0);
        setAreaGroup([
          {
            value: "all",
            label: "全て",
            count: 0,
          },
        ]);
      }
    };

    fetchDownloadList();
  }, [setTotalFiles, setAreaGroup, onFileNameOptionsChange]);

  const handleDownload = (url) => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url; // ここに CloudFront URL
    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 60000);
  };

  const handleCsvDownload = (row) => {
    if (!row) return;
    if (row.csvUrl) {
      handleDownload(row.csvUrl);
      return;
    }

    let baseUrl = process.env.REACT_APP_PICTREE_DATA_BASE_URL;

    // ZIP と同じ配信ドメインを優先して CSV 取得先を揃える
    if (row.url) {
      try {
        baseUrl = new URL(row.url).origin;
      } catch (e) {
        console.warn("Failed to parse row.url, fallback to env base URL:", e);
      }
    }

    const zipFileName = row.url ? row.url.split("/").pop() : "";
    const csvFileName =
      zipFileName && /\.zip$/i.test(zipFileName)
        ? zipFileName.replace(/\.zip$/i, ".csv")
        : `${row.name || ""}.csv`;
    const csvUrl = new URL(`/tepco/download/${csvFileName}`, baseUrl).toString();
    handleDownload(csvUrl);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    setCurrentPageNumber(value);
  };

  // ---- フィルター適用ロジック ----
  const selectedAreas = normalizeSelectedAreas(
    filterDownload &&
      filterDownload.areaGroup &&
      filterDownload.areaGroup.value
  );

  const dateRange = filterDownload && filterDownload.dateRange;
  const hasDateRange = dateRange && dateRange.start && dateRange.end;
  const fileNameKeywords =
    filterDownload && Array.isArray(filterDownload.fileNameKeywords)
      ? filterDownload.fileNameKeywords
          .map((keyword) =>
            typeof keyword === "string" ? keyword.trim().toLowerCase() : ""
          )
          .filter(Boolean)
      : [];
  const hasFileNameKeywords = fileNameKeywords.length > 0;

  // 1) 県域フィルタ
  const areaFiltered =
    selectedAreas.length === 0 || selectedAreas.includes("all")
      ? tableData
      : tableData.filter((item) =>
          selectedAreas.includes(normalizeAreaValue(item.area))
        );

  // 2) 日付レンジフィルタ（文字列 YYYY-MM-DD 同士なので比較OK）
  const dateFiltered = hasDateRange
    ? areaFiltered.filter((item) => {
        if (!item.date) return false;
        return item.date >= dateRange.start && item.date <= dateRange.end;
      })
    : areaFiltered;

  // 3) ファイル名キーワード（OR 条件の部分一致）
  const filteredData = hasFileNameKeywords
    ? dateFiltered.filter((item) => {
        const fileName = String(item.fileName || "").toLowerCase();
        return fileNameKeywords.some((keyword) => fileName.includes(keyword));
      })
    : dateFiltered;

  // ページングは「絞り込まれた件数」を基準にする
  const totalFiltered = filteredData.length;
  const pageCount = Math.ceil(totalFiltered / itemsPerPage) || 1;

  useEffect(() => {
    if (typeof onFilteredCountChange === "function") {
      onFilteredCountChange(totalFiltered);
    }
  }, [totalFiltered, onFilteredCountChange]);

  // 現在ページに表示するデータだけを取り出す
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent={"center"}
        className={open ? classes.tableWrapperSlim : classes.tableWrapper}
      >
        <Grid item>
          <div className={classes.tableBlock}>
            <div className={classes.tableBlockHeader}>
              <span /> {/* 左側を空にして右寄せ維持（不要なら消してOK） */}
              <Typography variant="caption" className={classes.tableNote}>
                *ダウンロードのファイルデータはサンプルです。
              </Typography>
            </div>
            <TableContainer component={Paper}>
              <Table stickyHeader className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell align="center">ファイル名</TableCell>
                    <TableCell align="center">作成日時</TableCell>
                    <TableCell align="center">画像ダウンロード</TableCell>
                    <TableCell align="center">CSVダウンロード</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length > 0 &&
                    paginatedData.map((row, index) => (
                      <TableRow
                        className={classes.tableRow}
                        key={row.id || index}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          style={{ width: "10%" }}
                          align="center"
                        >
                          {startIndex + index + 1}
                        </TableCell>

                        <TableCell style={{ width: "30%" }} align="center">
                          <span>{row.fileName}</span>
                          {(Number.isFinite(row.reportCount) ||
                            Number.isFinite(row.imageCount)) && (
                            <span className={classes.fileMeta}>
                              （報告
                              {Number.isFinite(row.reportCount)
                                ? row.reportCount
                                : "-"}
                              件 / 画像
                              {Number.isFinite(row.imageCount)
                                ? row.imageCount
                                : "-"}
                              枚）
                            </span>
                          )}
                        </TableCell>

                        <TableCell style={{ width: "20%" }} align="center">
                          {row.date}
                        </TableCell>
                        <TableCell style={{ width: "20%" }} align="center">
                          <button
                            type="button"
                            onClick={() => handleDownload(row.url)}
                            style={{
                              cursor: "pointer",
                              color: "blue",
                              textDecoration: "underline",
                              background: "none",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            画像ダウンロード (zip)
                          </button>
                        </TableCell>
                        <TableCell style={{ width: "20%" }} align="center">
                          <button
                            type="button"
                            onClick={() => handleCsvDownload(row)}
                            style={{
                              cursor: "pointer",
                              color: "blue",
                              textDecoration: "underline",
                              background: "none",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            CSVダウンロード
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}

                  {paginatedData.length === 0 && (
                    <TableRow className={classes.tableRowNone}>
                      <TableCell colSpan={5} className={classes.noDataCell}>
                        データがありません
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Grid>

        {/* 件数が 0 のときはページネーション非表示 */}
        {totalFiltered > 0 && (
          <Grid item>
            <div className={classes.pagination}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="secondary"
              />
            </div>
          </Grid>
        )}
      </Grid>
    </>
  );
};

DownloadTable.propTypes = {
  currentPageNumber: PropTypes.number,
  setCurrentPageNumber: PropTypes.func.isRequired,
  open: PropTypes.bool,
  setTotalFiles: PropTypes.func.isRequired,
  setAreaGroup: PropTypes.func.isRequired,
  filterDownload: PropTypes.object,
  onFileNameOptionsChange: PropTypes.func,
  onFilteredCountChange: PropTypes.func,
};

const mapStateToProps = (state) => ({
  currentPageNumber: state.home.currentPageNumber,
  filterDownload: state.download.filterDownload,
});

const mapDispatchToProps = (dispatch) => ({
  setTotalFiles: (param) => dispatch(setTotalFiles(param)),
  setAreaGroup: (param) => dispatch(setAreaGroup(param)),
  setCurrentPageNumber: (param) => dispatch(setCurrentPageNumber(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(DownloadTable));
